import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity as ActivityIcon, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { humanizeActivity, activityLink } from "@/lib/activityLabels";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { PageHeader } from "@/components/shared/PageChrome";
import { ViewTabs, useListParams } from "@/components/shared/ListChrome";
import { Pagination } from "@/components/shared/StackedList";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;
const VIEWS = [{ value: "ALL", label: "All activity" }, { value: "enquiry", label: "Requests" }, { value: "booking", label: "Bookings" }, { value: "document", label: "Documents" }];
const dayKey = (d) => new Date(d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export default function ClientActivity() {
  const { view, page, setView, setPage } = useListParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setData(null);
    api.get("/client/activity", { params: { page, page_size: PAGE_SIZE, entity_type: view === "ALL" ? undefined : view } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [page, view]);

  if (error) return <ErrorState testId="client-activity-error" />;

  const groups = data ? data.items.reduce((acc, a) => { const k = dayKey(a.created_at); (acc[k] = acc[k] || []).push(a); return acc; }, {}) : {};

  return (
    <div data-testid="client-activity-page" className="space-y-5">
      <PageHeader title="Activity" subtitle="A full audit trail of everything that has happened on your account." chip={data && <span data-testid="client-activity-total" className="rounded-full border border-jigsaw-border px-2.5 py-0.5 text-[12px] text-jigsaw-muted">{data.total} events</span>} />
      <ViewTabs testId="client-activity-views" tabs={VIEWS} value={view} onChange={setView} counts={data?.counts} />

      {!data ? <LoadingState testId="client-activity-loading" /> : data.items.length === 0 ? (
        <EmptyState testId="client-activity-empty" icon={ActivityIcon} title="No activity yet" description="Activity will appear here once requests, bookings or documents change." />
      ) : (
        <>
          <div className="space-y-5">
            {Object.entries(groups).map(([day, rows]) => (
              <section key={day} data-testid="client-activity-day">
                <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">{day}</h2>
                <ol className="relative divide-y divide-jigsaw-border rounded-[10px] border border-jigsaw-border bg-white">
                  {rows.map((a) => {
                    const link = activityLink(a.entity_type, a.entity_id);
                    return (
                      <li key={a.id} data-testid={`client-activity-row-${a.id}`} onClick={link ? () => navigate(link) : undefined}
                        className={cn("flex items-center gap-3 px-4 py-3", link && "cursor-pointer hover:bg-jigsaw-bg")}>
                        <span className="h-2 w-2 shrink-0 rounded-full bg-jigsaw-orange" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-jigsaw-navy">{humanizeActivity(a.action)}</p>
                          <p className="text-xs text-jigsaw-muted">{new Date(a.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}{a.entity_reference ? <> · <span className="font-mono-ref">{a.entity_reference}</span></> : null}</p>
                        </div>
                        {link && <ArrowRight className="h-4 w-4 shrink-0 text-jigsaw-muted" />}
                      </li>
                    );
                  })}
                </ol>
              </section>
            ))}
          </div>
          <Pagination testId="client-activity-pagination" page={data.page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} />
        </>
      )}
    </div>
  );
}
